// Embed-snippet generator (Phase 5.7 / master plan §608). Displayed on the
// edit page of an existing agent, since the agent id needs to be live to
// paste into a customer's site. Two variants:
//   • <script> — the primary install path (SRI-signed CDN bundle).
//   • npm — for React/Next.js apps that want to control mounting.
//
// The snippet strings come from @cortex/shared's buildWidgetSnippet — the
// same generator the docs quickstart uses, so changes to the data-*
// surface land in both places in one commit.

import { useMemo, useState } from 'react';
import { Check, Copy, AlertTriangle } from 'lucide-react';
import {
  buildWidgetNpmSnippet,
  buildWidgetSnippet,
} from '@cortex/shared';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card.js';
import { Button } from '../../components/ui/button.js';
import {
  WIDGET_SRI,
  WIDGET_SRI_LIVE,
  WIDGET_URL,
} from '../../lib/widget-integrity.js';

// Runtime-resolved at window load; kept as a constant so the snippet text
// is deterministic for tests. The api-client picks its base from env at
// runtime, but the *snippet* is a string the customer pastes — the docs
// always point at the prod CDN, so we do too.
const DEFAULT_API_BASE = 'https://api.cortexlayer.dev';

type Tab = 'script' | 'npm';

export interface WidgetInstallCardProps {
  agentId: string;
}

export function WidgetInstallCard({ agentId }: WidgetInstallCardProps) {
  const [tab, setTab] = useState<Tab>('script');

  const scriptSnippet = useMemo(
    () =>
      buildWidgetSnippet({
        cdnUrl: WIDGET_URL,
        integrity: WIDGET_SRI,
        apiBase: DEFAULT_API_BASE,
        agentId,
      }),
    [agentId],
  );
  const npmSnippet = useMemo(
    () => buildWidgetNpmSnippet({ agentId, apiBase: DEFAULT_API_BASE }),
    [agentId],
  );

  const active = tab === 'script' ? scriptSnippet : npmSnippet;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Install widget</CardTitle>
        <CardDescription>
          Paste into the host site to mount the floating chat widget. The SRI
          hash is rebuilt on every CDN publish — copy fresh if the hash changes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!WIDGET_SRI_LIVE && (
          <div
            role="status"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Widget bundle not built in this workspace yet — the <code>integrity</code>
              hash below is a placeholder. Run{' '}
              <code>pnpm --filter @cortex/widget build</code> for a real hash.
            </span>
          </div>
        )}

        <div role="tablist" aria-label="Install method" className="inline-flex rounded-md border p-0.5">
          <TabButton
            label="Script tag"
            active={tab === 'script'}
            onClick={() => { setTab('script'); }}
          />
          <TabButton
            label="npm"
            active={tab === 'npm'}
            onClick={() => { setTab('npm'); }}
          />
        </div>

        <SnippetBlock
          ariaLabel={tab === 'script' ? 'HTML script snippet' : 'npm usage snippet'}
          text={active}
        />

        <p className="text-xs text-muted-foreground">
          {tab === 'script'
            ? 'Drop this into your site\u2019s <head>. The widget auto-initialises when the DOM is ready.'
            : 'Use in a bundled React / Next.js app when you need to control when the widget mounts.'}
        </p>
      </CardContent>
    </Card>
  );
}

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, active, onClick }: TabButtonProps) {
  return (
    <button
      role="tab"
      type="button"
      aria-selected={active}
      onClick={onClick}
      className={
        'rounded-sm px-3 py-1 text-xs font-medium transition-colors ' +
        (active
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:text-foreground')
      }
    >
      {label}
    </button>
  );
}

interface SnippetBlockProps {
  text: string;
  ariaLabel: string;
}

function SnippetBlock({ text, ariaLabel }: SnippetBlockProps) {
  const [copied, setCopied] = useState(false);

  async function onCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1500);
    } catch {
      // Clipboard denied (insecure context, permission policy) — leave the
      // snippet visible so the user can select + copy manually. A thrown
      // error here would be noisier than silently failing.
    }
  }

  return (
    <div className="relative">
      <pre
        aria-label={ariaLabel}
        className="max-h-96 overflow-auto rounded-md border bg-muted/30 p-4 pr-16 font-mono text-xs"
      >
        <code>{text}</code>
      </pre>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="absolute right-2 top-2 h-7 gap-1 px-2 text-xs"
        onClick={() => void onCopy()}
        aria-label="Copy snippet"
      >
        {copied ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
